import { motion, useScroll, useTransform } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { isPromoActive } from '@/lib/promo';
import { CountdownHero } from '@/components/PromoCountdown';
const HeroSection = () => {
  const sectionRef = useRef(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showVideo, setShowVideo] = useState(false);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start']
  });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  // Load the background video once the page has finished loading (keeps LCP fast,
  // still autoplays on mobile because it is muted + inline).
  useEffect(() => {
    let cancelled = false;
    const start = () => {
      if (!cancelled) setShowVideo(true);
    };
    const schedule = () => {
      const idle = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback;
      if (idle) idle(start, { timeout: 1200 });
      else window.setTimeout(start, 400);
    };
    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true });
    return () => {
      cancelled = true;
      window.removeEventListener('load', schedule);
    };
  }, []);


  // Mobile browsers sometimes ignore the autoplay attribute on a freshly mounted
  // element: force playback (muted + inline keeps it policy-compliant).
  useEffect(() => {
    if (!showVideo) return;
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const tryPlay = () => {
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };
    tryPlay();
    v.addEventListener('loadeddata', tryPlay);
    document.addEventListener('visibilitychange', tryPlay);
    const onTouch = () => tryPlay();
    document.addEventListener('touchstart', onTouch, { once: true, passive: true });
    return () => {
      v.removeEventListener('loadeddata', tryPlay);
      document.removeEventListener('visibilitychange', tryPlay);
      document.removeEventListener('touchstart', onTouch);
    };
  }, [showVideo]);


  // Hero restored to the larger pre-change size
  return <section ref={sectionRef} className="relative h-[95vh] min-h-[700px] max-h-[900px] flex items-center justify-center overflow-hidden bg-background">
      {/* Video Background */}
      <div className="absolute inset-0 z-0 bg-background">
        <motion.div style={{
        y
      }} className="w-full h-[120%] -mt-[10%]">
          {showVideo ? (
            <video ref={videoRef} autoPlay muted loop playsInline preload="auto" disablePictureInPicture poster="/hero-poster.jpg" className="w-full h-full object-cover">
              <source src="/videos/loop.mp4" type="video/mp4" />
            </video>
          ) : (
            <img src="/hero-poster.jpg" alt="" aria-hidden="true" fetchPriority="high" decoding="async" className="w-full h-full object-cover" />
          )}
        </motion.div>
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/60 to-background" />
      </div>


      {/* Content */}
      <motion.div style={{
      opacity
    }} className="relative z-10 container-premium text-center">
        {/* Promo pill removed — CTA below */}


        {/* Main title */}
        {/* pb prevents clipping of descenders during the slide-up animation */}
        <div className="overflow-hidden pb-3 mb-6">
          <motion.h1 className="font-serif text-4xl md:text-6xl lg:text-7xl xl:text-8xl font-semibold leading-tight" initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} transition={{
          duration: 1,
          delay: 0.15,
          ease: [0.25, 0.4, 0.25, 1]
        }}>
            <span className="block">Trasformiamo la tua</span>
            <span className="block text-gradient-gold">azienda</span>
          </motion.h1>
        </div>

        {/* Subtitle */}
        <motion.p className="text-lg md:text-xl lg:text-2xl text-muted-foreground max-w-2xl mx-auto mb-12" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.35
      }}>
          E ti aiutiamo ad aumentare il tuo fatturato<br />
          <span className="text-foreground">con strategie digitali che funzionano davvero</span>
        </motion.p>

        {/* CTA Buttons */}
        <motion.div className="flex flex-col sm:flex-row items-center justify-center gap-4" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.5
      }}>
          {isPromoActive() ? (
            <Link to="/promozioni" className="btn-premium inline-flex items-center gap-3">
              <span>Scopri Promo</span>
              <span className="hidden sm:inline-block w-px h-4 bg-current opacity-30" aria-hidden="true" />
              <CountdownHero className="text-sm md:text-base opacity-95" />
            </Link>
          ) : (
            <a href="/contatti" className="btn-premium">
              <span>Prenota una Call Gratuita</span>
            </a>
          )}
          <a href="#servizi" className="btn-ghost">
            Scopri i Servizi
          </a>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1.2
    }}>
        
      </motion.div>
    </section>;
};
export default HeroSection;